import type { AccessAnswer } from "@/lib/directory";

/**
 * Which apps the grid shows: only the ones you could sign in to.
 *
 * There is no "show everything" mode. An app you are not authorized to use is not in
 * the grid at all. The one exception is `requestableByAccess` below: an app a
 * superadmin has made requestable is listed, separately, under "Apps you can request".
 *
 * ⚠ PRESENTATION ONLY, still. Home does not grant or withhold access to anything —
 * every card is a plain link to another origin, and the target app's own gate is what
 * decides entry. Hiding a card removes clutter; it removes no capability, and
 * somebody with the URL is exactly as able to open the app either way. This is why
 * the filter lives in the client rather than in `GET /apps`: treating it as a
 * security boundary would be a lie about what it does.
 */

/** The minimum an app must tell the filter about itself. */
export interface FilterableApp {
  id: string;
  /**
   * Every employee may use this app without being granted anything first.
   *
   * ⚠ Not derivable from the access answer, which is the whole reason the flag has
   * to exist — see `verdictFor`.
   */
  openToAll: boolean;
}

/**
 * What we know about one app, reduced to the question the filter asks.
 *
 * ⚠ Three states, not two. Conflating "unknown" with "no" is the mistake that makes
 * this feature harmful, and it is an easy one to make — see below.
 */
export type AccessVerdict = "mine" | "not-mine" | "unknown";

export function verdictFor(
  app: FilterableApp,
  access: AccessAnswer | undefined,
): AccessVerdict {
  // ⚠ FIRST, and independent of anything the app reported.
  //
  // An open app auto-provisions on first sign-in: anybody with a Clerk session can
  // walk in and a record is created for them there and then. So its directory
  // endpoint answers `registered: false` for somebody who has simply never opened it
  // — a string identical to a real refusal, which no probe can tell apart. Asking the
  // endpoint would therefore hide these apps from precisely the people they exist
  // for, and the effect compounds: a new employee's first visit to Home would show
  // them an empty page.
  if (app.openToAll) return "mine";

  // `undefined` = the access query is still in flight. `null` = that app returned
  // nothing usable.
  //
  // ⚠ `null` does NOT mean "no access". Home's `fetchAccess` returns null for an app
  // that never implemented /internal/directory/*, for one whose token is missing or
  // wrong, AND for one that is simply DOWN or slow enough to hit the 5s timeout.
  // Reading null as "no access" would make an app silently disappear from someone's
  // launcher during an outage — at the exact moment they are trying to work out why
  // it will not load, and looking to them like their access was revoked overnight.
  //
  // So: hide only on a definitive negative. Anything we are unsure of stays put.
  if (access === undefined || access === null) return "unknown";

  // ⚠ A suspension hides the app, and this branch must come before the one below
  // because the server sends `registered: false` for a suspended user (it computes
  // `registered && !deactivated`). Without it, the read is right by accident today
  // and wrong the moment that server-side expression changes.
  //
  // Someone suspended from an app cannot sign in to it, and the rule here is exactly
  // "apps you could sign in to". Per-app suspension only: a Clerk-level suspension
  // stops the person reaching Home at all, so it never gets this far.
  if (access.suspended) return "not-mine";

  return access.registered ? "mine" : "not-mine";
}

/** Drops the apps this person could not sign in to. */
export function filterByAccess<T extends FilterableApp>(
  apps: T[],
  accessOf: (id: string) => AccessAnswer | undefined,
): T[] {
  return apps.filter((app) => verdictFor(app, accessOf(app.id)) !== "not-mine");
}

/**
 * The apps listed under "Apps you can request": ones the app itself says you cannot
 * use, that a superadmin has opted in.
 *
 * ⚠ Only on a definitive "not-mine" — the same verdict that hides the app from the
 * grid, so an app is in exactly one of the two places, never both and never neither.
 * An unknown app stays in the grid: offering "Request access" for an app that is
 * merely down would tell somebody who already has access that they don't.
 *
 * A suspension counts as "not-mine" here too, so somebody suspended from an app can
 * ask to be let back in. `openToAll` apps are always "mine" and so never appear.
 */
export function requestableByAccess<T extends FilterableApp & { requestable: boolean }>(
  apps: T[],
  accessOf: (id: string) => AccessAnswer | undefined,
): T[] {
  return apps.filter(
    (app) => app.requestable && verdictFor(app, accessOf(app.id)) === "not-mine",
  );
}
